import React, { PropsWithChildren, useCallback, useState } from 'react';
import { PopupStyle as S } from './Popup.style';
import { HStack, VStack } from 'components/layouts/primitives/Stack/Stack';
import { Notification, NotificationProps } from 'components/Notification/Notification';
import { Box } from 'components/layouts/primitives/Box/Box';
import { PButton, PButtonProps } from 'components/prime/PButton/PButton';
import { Badge, BadgeProps } from 'components/Badge/Badge';
import { Text } from 'components/Text/Text';

export type PopupProps = {
  title: string;
  onDismiss?: () => void;
  badgeProps: Pick<BadgeProps, 'label' | 'bgColor' | 'bgColorAlpha' | 'color'>;
  bannerImgSrc: string;
  notificationProps?: Pick<NotificationProps, 'description'>;
  footerActions: (Pick<
    PButtonProps,
    'label' | 'severity' | 'to' | 'href' | 'target' | 'rel' | 'onClick' | 'iconLeft' | 'iconRight'
  > & {
    preventDismiss?: boolean;
  })[];
};

export const Popup: React.FC<PropsWithChildren<PopupProps>> = ({
  title,
  badgeProps,
  onDismiss,
  bannerImgSrc,
  notificationProps,
  footerActions,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const hide = useCallback(() => {
    setIsVisible(false);
    onDismiss?.();
  }, [onDismiss]);
  return isVisible ? (
    <S.Popup
      onHide={hide}
      closeButtonProps={{ size: 'sm' }}
      customHeader={
        <VStack className="popup-header">
          <img src={bannerImgSrc} alt={title} />
          <Box padding={['paddingXL', 'paddingXL', 'paddingNone']}>
            <VStack gap="gap2" justify="flex-start">
              {!!badgeProps && (
                <Box>
                  <Badge {...badgeProps} />
                </Box>
              )}
              <Text
                size="fontSizeLG"
                lineHeight="lineHeightXL"
                weight="medium"
                color="neutral600"
                ellipsis={3}
              >
                {title}
              </Text>
            </VStack>
          </Box>
        </VStack>
      }
      footer={
        <VStack gap="gap4">
          {!!notificationProps && <Notification layoutType="compact" hideIcon {...notificationProps} />}
          {!!footerActions?.length && (
            <HStack gap="gap2" align="center">
              {footerActions.slice(0, 2).map(({ preventDismiss, ...action }) => (
                <PButton
                  size="md"
                  className="flex-1"
                  {...action}
                  onClick={(e) => {
                    action.onClick?.(e);
                    !preventDismiss && hide();
                  }}
                />
              ))}
            </HStack>
          )}
        </VStack>
      }
      visible
      maskClassName="popup-mask"
    >
      {children}
    </S.Popup>
  ) : null;
};
